import React from 'react';

import { Icon } from '@rbilabs/components-library/build/components/icon';
import { getLanguages } from '@rbilabs/intl';
import { isNil } from 'lodash';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import ListOfLinks from 'components/app-header/dropdown/list-of-links';
import LanguageSelector from 'components/language-selector';
import Link from 'components/link';
import { BackArrowHistory } from 'components/navigation/back-arrow';
import { useFeatureNavigation } from 'hooks/use-feature-navigation';
import useMediaQuery from 'hooks/use-media-query';
import { useAuthContext } from 'state/auth';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { primitive } from 'styles/constants/primitives';
import { FALLBACK_LANGUAGE } from 'utils/constants';
import { brand } from 'utils/environment';
import { routes } from 'utils/routing';

import Disclaimer from './disclaimer';

const AboutContainer = styled.div`
  padding-block-start: 0;
  padding: ${primitive.$spacing7};

  ${Styles.desktop} {
    margin-block-start: 6em;
  }
`;

const AboutLanguageSelectorContainer = styled.div`
  border-block-end: 1px solid ${Styles.color.grey.five};
  padding-block-end: ${primitive.$spacing4};
`;

const LanguageSelectIntro = styled.div`
  text-transform: none;
`;

const AboutAction = styled(Link)`
  text-decoration: none;
  font-size: 1rem;
  display: flex;
  margin: ${primitive.$spacing2} 0;
  & span {
    font-family: ${Styles.fontFamily.brand};
  }
`;

const StyledIcon = styled.div`
  position: relative;
  inset-inline-end: ${primitive.$spacing1};
`;

interface IAboutProps {
  borderColor?: string;
  showActions?: boolean;
  sectionHeadings?: boolean;
  showLanguageSelector?: boolean;
}

const About = ({
  borderColor,
  showActions,
  sectionHeadings,
  showLanguageSelector,
}: IAboutProps) => {
  const isMobile = useMediaQuery('mobile');
  const {
    featureAboutMenuEntriesDesktop: aboutMenuEntries,
    featureAboutMenuEntriesMobile: aboutMenuEntriesMobile,
  }: any = useFeatureNavigation();
  const { isAuthenticated } = useAuthContext();
  const { formatMessage } = useIntl();
  const { region } = useLocale();
  const mParticle = useMParticleContext();
  const enableDiagnosticTools = useFlag(LaunchDarklyFlag.ENABLE_DIAGNOSTIC_TOOLS);
  const enableFindRestaurantLink = useFlag(LaunchDarklyFlag.ENABLE_FIND_RESTAURANT_LINK);
  const isLocalizationDisabled = useFlag(LaunchDarklyFlag.DISABLE_LOCALIZATION);
  const enableSignUpFlag = useFlag(LaunchDarklyFlag.ENABLE_SIGN_UP);
  const { navigate } = useLocationContext();
  const enableSignUp = isNil(enableSignUpFlag) || enableSignUpFlag;
  const createOnStaticPageClick = (attr: any) => {
    mParticle.trackEvent({
      name: CustomEventNames.BUTTON_CLICK_STATIC_PAGES,
      type: EventTypes.Navigation,
      attributes: attr,
    });
  };
  const menuEntries = isMobile ? aboutMenuEntriesMobile : aboutMenuEntries;
  const ActionsWrapper = styled.div`
    border-block-end: 1px solid ${borderColor};
    padding-block-end: 1rem;
  `;
  const regionSupportedLanguages = getLanguages(brand(), region) ?? [FALLBACK_LANGUAGE];
  const shouldDisplayForRegion = !isLocalizationDisabled;
  const hasMultipleLanguagesForRegion = region && regionSupportedLanguages.length > 1;

  const shouldShowLanguageSelector =
    showLanguageSelector && shouldDisplayForRegion && hasMultipleLanguagesForRegion;

  return (
    <>
      <Helmet title={formatMessage({ id: 'about' })} />
      <BackArrowHistory />
      <AboutContainer>
        {shouldShowLanguageSelector && (
          <AboutLanguageSelectorContainer>
            <LanguageSelectIntro>{formatMessage({ id: 'languageAndRegion' })}</LanguageSelectIntro>
            <LanguageSelector color={Styles.color.black} />
          </AboutLanguageSelectorContainer>
        )}
        {showActions && (
          <ActionsWrapper>
            {enableSignUp && (
              <>
                {isAuthenticated() ? (
                  <AboutAction data-testid="account-link" to={routes.account}>
                    <StyledIcon>
                      <Icon icon="account" color="icon-default" width="24px" aria-hidden />
                    </StyledIcon>
                    <span>{formatMessage({ id: 'account' })}</span>
                  </AboutAction>
                ) : (
                  <AboutAction data-testid="signup-link" to={routes.signUp}>
                    <StyledIcon>
                      <Icon icon="account" color="icon-default" width="24px" aria-hidden />
                    </StyledIcon>
                    <span>{formatMessage({ id: 'signInOrCreateAccount' })}</span>
                  </AboutAction>
                )}
              </>
            )}
            {enableFindRestaurantLink && (
              <AboutAction to={routes.storeLocator}>
                <StyledIcon>
                  <Icon icon="location" color="icon-default" width="24px" aria-hidden />
                </StyledIcon>
                <span>{formatMessage({ id: 'findARestaurant' })}</span>
              </AboutAction>
            )}
          </ActionsWrapper>
        )}
        {menuEntries && (
          <ListOfLinks
            data-testid="links-list"
            title={menuEntries.name?.locale || ''}
            entries={(menuEntries.pages || []).concat({
              title: formatMessage({ id: 'aboutApp' }),
              path: { current: routes.aboutApp },
            })}
            trackStaticPage={createOnStaticPageClick}
            borderColor={borderColor}
            sectionHeadings={sectionHeadings}
            navigate={navigate}
          />
        )}
        <Disclaimer showCalorieDisclaimer={false} />
        {enableDiagnosticTools && (
          <Link to={routes.diagnostics}>{formatMessage({ id: 'diagnostics' })}</Link>
        )}
      </AboutContainer>
    </>
  );
};

export default About;
